import axios from "axios";
import { store } from '../store/store';

const apiUrl = process.env.REACT_APP_API_URL;

if (!apiUrl) {
  console.error('API URL is not defined. Make sure REACT_APP_API_URL is set in your .env file');
}

const getAuthHeaders = () => {
  // Get token from Redux store first
  const state = store.getState();
  const token = state.auth.token;

  // Fallback to cookie if token not in store
  if (!token) {
    const cookieToken = document.cookie
      .split("; ")
      ?.find((row) => row.startsWith("token="))
      ?.split("=")[1];

    if (!cookieToken) {
      throw new Error('No authentication token found');
    }
    return { Authorization: `Bearer ${cookieToken}` };
  }

  return { Authorization: `Bearer ${token}` };
};

export const signIn = async (data) => {
  try {
    const response = await axios.post(`${apiUrl}/user/login`, {
      username: data.username,
      password: data.password
    });
    if (response.data.token) {
      document.cookie = `token=${response.data.token}; path=/`;
    }
    return response.data;
  } catch (error) {
    console.error("Error occurred during signIn API call:", error);
    throw error;
  }
};

export const getApi = async (url) => {
  try {
    console.log('Making API call to:', `${apiUrl}/user/${url}`);
    const config = {
      headers: getAuthHeaders(),
    };
    console.log('With headers:', config.headers);
    
    const response = await axios.get(`${apiUrl}/user/${url}`, config);
    console.log('API Response:', response.data);
    return response.data;
  } catch (error) {
    console.error("API Error:", error);
    if (error.message === 'No authentication token found' || error.response?.status === 401) {
      window.location.href = '/login';
    }
    throw error;
  }
};

export const postApi = async (url, data) => {
  try {
    const config = {
      headers: getAuthHeaders(),
    };
    const response = await axios.post(`${apiUrl}/user/${url}`, data, config);
    return response.data;
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

export const updateApi = async (url, data) => {
  try {
    const config = {
      headers: getAuthHeaders(),
    };
    const response = await axios.put(`${apiUrl}/user/${url}`, data, config);
    return response.data;
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

export const deleteApi = async (url) => {
  try {
    const config = {
      headers: getAuthHeaders(),
    };
    const response = await axios.delete(`${apiUrl}/user/${url}`, config);
    return response.data;
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

export const patchApi = async (url, data) => {
  try {
    const config = {
      headers: getAuthHeaders(),
    };
    const response = await axios.patch(`${apiUrl}/user/${url}`, data, config);
    return response.data;
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

export const sendVerificationOTPs = async (data) => {
  try {
    const response = await axios.post(`${apiUrl}/user/send-verification-otps`, {
      email: data.email,
      mobileNumber: data.mobileNumber
    });
    return response.data;
  } catch (error) {
    console.error("Error occurred during OTP sending:", error);
    throw error;
  }
};

export const verifyOTPs = async (data) => {
  try {
    const response = await axios.post(`${apiUrl}/user/verify-otp`, {
      verificationToken: data.verificationToken,
      emailOTP: data.emailOTP,
      mobileOTP: data.mobileOTP
    });
    return response.data;
  } catch (error) {
    console.error("Error occurred during OTP verification:", error);
    throw error;
  }
};

export const registerUser = async (data) => {
  try {
    const response = await axios.post(`${apiUrl}/user/register`, {
      name: data.name,
      email: data.email,
      mobileNumber: data.mobileNumber,
      gender: data.gender,
      password: data.password,
      registrationToken: data.registrationToken,
      referedBy: data.referedBy || '',
      agentId: data.agentId
    });
    return response.data;
  } catch (error) {
    console.error("Error occurred during registration:", error);
    throw error;
  }
};

export const getKYCDetails = async () => {
  try {
    const response = await axios.get(`${apiUrl}/user/kyc/details`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching KYC details:", error);
    throw error;
  }
};

export const getKYCDocument = async (type) => {
  try {
    const response = await axios.get(`${apiUrl}/user/kyc/document/${type}`, {
      headers: getAuthHeaders(),
      responseType: 'blob'
    });
    return URL.createObjectURL(response.data);
  } catch (error) {
    console.error(`Error fetching KYC document (${type}):`, error);
    throw error;
  }
};

export const getBankAccounts = async () => {
  try {
    const response = await axios.get(`${apiUrl}/user/bank-accounts`, {
      headers: getAuthHeaders()
    });
    return {
      success: response.data.success,
      data: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    console.error("Error fetching bank accounts:", error);
    throw error;
  }
};

export const addBankAccount = async (data) => {
  try {
    const response = await axios.post(`${apiUrl}/user/bank-accounts`, data, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error("Error adding bank account:", error);
    throw error;
  }
};

export const updateBankAccount = async (accountId, data) => {
  try {
    const response = await axios.put(`${apiUrl}/user/bank-accounts/${accountId}`, data, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error("Error updating bank account:", error);
    throw error;
  }
};

export const getPaymentMethods = async () => {
  try {
    const response = await axios.get(`${apiUrl}/user/payment-methods`, {
      headers: getAuthHeaders()
    });
    return {
      success: response.data.success,
      data: response.data.data || response.data.paymentMethods || [],
      message: response.data.message
    };
  } catch (error) {
    console.error("Error fetching payment methods:", error);
    throw error;
  }
};

export const createDeposit = async (data) => {
  try {
    const payload = {
      amount: Number(data.amount),
      paymentMethodId: data.paymentMethodId,
      medium: data.medium,
      transactionId: data.transactionId
    };

    const response = await axios.post(`${apiUrl}/user/deposit`, payload, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error("Error creating deposit:", error);
    throw error;
  }
};

export const createWithdraw = async (data) => {
  try {
    const payload = {
      amount: Number(data.amount),
      bankAccountId: data.bankAccountId,
      medium: data.medium
    };

    const response = await axios.post(`${apiUrl}/user/withdraw`, payload, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error("Error creating withdrawal:", error);
    if (error.response?.data) {
      return error.response.data;
    }
    throw error;
  }
};

export const getTransactions = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add all query parameters
    if (params.type) queryParams.append('type', params.type);
    if (params.status) queryParams.append('status', params.status);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const url = `${apiUrl}/user/transactions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    
    const response = await axios.get(url, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching transactions:", error);
    throw error;
  }
};

export const getLiveForexRates = async (currencies = ['EURUSD', 'GBPUSD']) => {
  try {
    // Try to get API key from environment variable, with fallback for development
    let apiKey = process.env.REACT_APP_FASTFOREX_API_KEY;
    
    // Fallback for development if environment variable is not loaded
    if (!apiKey && process.env.NODE_ENV === 'development') {
      apiKey = '4161d3f0ff-87937badbc-t1uqcp';
      console.warn('Using fallback API key for development. Please ensure REACT_APP_FASTFOREX_API_KEY is set in .env file.');
    }
    
    console.log('Environment variables check:', {
      REACT_APP_FASTFOREX_API_KEY: process.env.REACT_APP_FASTFOREX_API_KEY,
      NODE_ENV: process.env.NODE_ENV,
      fallbackUsed: !process.env.REACT_APP_FASTFOREX_API_KEY && process.env.NODE_ENV === 'development',
      allEnvVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
    });
    
    if (!apiKey) {
      throw new Error('FastForex API key not found. Please set REACT_APP_FASTFOREX_API_KEY in your .env file. Current value: ' + process.env.REACT_APP_FASTFOREX_API_KEY);
    }

    // FastForex API endpoint for multiple currency rates using fetch-multi
    const quotes = [];
    
    // Fetch rates for each currency pair individually using fetch-multi
    for (const currency of currencies) {
      try {
        const baseCurrency = currency.substring(0, 3);
        const quoteCurrency = currency.substring(3, 6);
        const url = `https://api.fastforex.io/fetch-multi?from=${baseCurrency}&to=${quoteCurrency}&api_key=${apiKey}`;
        
        const response = await axios.get(url);
        
        if (response.data && response.data.results && response.data.base) {
          // The response structure is: {"base":"AUD","results":{"USD":0.65418},"updated":"2025-09-03 20:06:04","ms":1}
          const baseCurrency = response.data.base;
          const quoteCurrency = Object.keys(response.data.results)[0]; // Get the first (and only) quote currency
          const rate = response.data.results[quoteCurrency];
          
          if (rate) {
            quotes.push({
              base_currency: baseCurrency,
              quote_currency: quoteCurrency,
              mid: rate,
              bid: rate * 0.9999, // Approximate bid
              ask: rate * 1.0001  // Approximate ask
            });
          }
        }
      } catch (error) {
        console.warn(`Failed to fetch rate for ${currency}:`, error.message);
      }
    }
    
    if (quotes.length === 0) {
      throw new Error('No currency rates could be fetched from FastForex API');
    }
    
    return { quotes };
  } catch (error) {
    console.error("Error fetching live forex rates:", error);
    throw error;
  }
};

export const getLiveGoldRates = async (symbols = ['XAUUSD']) => {
  try {
    // Try to get API key from environment variable, with fallback for development
    let apiKey = process.env.REACT_APP_FASTFOREX_API_KEY;
    
    // Fallback for development if environment variable is not loaded
    if (!apiKey && process.env.NODE_ENV === 'development') {
      apiKey = '4161d3f0ff-87937badbc-t1uqcp';
      console.warn('Using fallback API key for development. Please ensure REACT_APP_FASTFOREX_API_KEY is set in .env file.');
    }
    
    console.log('Environment variables check (Gold):', {
      REACT_APP_FASTFOREX_API_KEY: process.env.REACT_APP_FASTFOREX_API_KEY,
      NODE_ENV: process.env.NODE_ENV,
      fallbackUsed: !process.env.REACT_APP_FASTFOREX_API_KEY && process.env.NODE_ENV === 'development',
      allEnvVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
    });
    
    if (!apiKey) {
      throw new Error('FastForex API key not found. Please set REACT_APP_FASTFOREX_API_KEY in your .env file. Current value: ' + process.env.REACT_APP_FASTFOREX_API_KEY);
    }

    // FastForex API endpoint for multiple currency rates using fetch-multi
    const quotes = [];
    
    // Fetch rates for each symbol individually using fetch-multi
    for (const symbol of symbols) {
      try {
        const baseCurrency = symbol.substring(0, 3);
        const quoteCurrency = symbol.substring(3, 6);
        const url = `https://api.fastforex.io/fetch-multi?from=${baseCurrency}&to=${quoteCurrency}&api_key=${apiKey}`;
        
        const response = await axios.get(url);
        
        if (response.data && response.data.results && response.data.base) {
          // The response structure is: {"base":"AUD","results":{"USD":0.65418},"updated":"2025-09-03 20:06:04","ms":1}
          const baseCurrency = response.data.base;
          const quoteCurrency = Object.keys(response.data.results)[0]; // Get the first (and only) quote currency
          const rate = response.data.results[quoteCurrency];
          
          if (rate) {
            quotes.push({
              base_currency: baseCurrency,
              quote_currency: quoteCurrency,
              mid: rate,
              bid: rate * 0.9999, // Approximate bid
              ask: rate * 1.0001  // Approximate ask
            });
          }
        }
      } catch (error) {
        console.warn(`Failed to fetch rate for ${symbol}:`, error.message);
      }
    }
    
    if (quotes.length === 0) {
      throw new Error('No currency rates could be fetched from FastForex API');
    }
    
    return { quotes };
  } catch (error) {
    console.error("Error fetching live gold rates:", error);
    throw error;
  }
};